// Generated from products/analytics/shared/collectionCrypto.ts; run analytics generate:site-runtime.
import { z } from "zod";
import { JSONR } from "./jsonr.gen";
import { AnalyticsPublicKeySchema, BrowserAnalyticsConfigSchema } from "./domainConfig.gen";
import type { BrowserAnalyticsConfig } from "./domainConfig.gen";
const BinaryScopeSchema = BrowserAnalyticsConfigSchema.pick({ hostname: true, path: true });

// This shared key is intentionally embedded in the browser, Worker and collector.
const keyHex = "fe3f618022c2b0cce4ca4c2aab4d4e740523afa68ab319da268a152b80ff4aa6";
const encoded = (max: number) =>
  z
    .string()
    .min(1)
    .max(max)
    .regex(/^[A-Za-z0-9_-]+$/);
export const EncryptedRequestSchema = z.strictObject({
  version: z.literal(1),
  iv: encoded(16),
  ciphertext: encoded(87403),
});
export const EncryptedResponseSchema = EncryptedRequestSchema;
export type EncryptedRequest = z.infer<typeof EncryptedRequestSchema>;
export type EncryptedResponse = z.infer<typeof EncryptedResponseSchema>;
const textEncoder = new TextEncoder();
let keyPromise: Promise<CryptoKey> | undefined;
function key(): Promise<CryptoKey> {
  if (!keyPromise) {
    const bytes = Uint8Array.from(keyHex.match(/../g) ?? [], (value) => Number.parseInt(value, 16));
    keyPromise = crypto.subtle.importKey("raw", bytes, { name: "AES-GCM" }, false, [
      "encrypt",
      "decrypt",
    ]);
  }
  return keyPromise;
}
function encode(bytes: Uint8Array): string {
  let raw = "";
  for (const byte of bytes) raw += String.fromCharCode(byte);
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function decode(value: string, max: number): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9_-]+$/.test(value) || value.length > Math.ceil((max * 4) / 3))
    throw new Error("Invalid encoding");
  let raw: string;
  try {
    raw = atob(
      value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (value.length % 4)) % 4),
    );
  } catch {
    throw new Error("Invalid encoding");
  }
  const result = Uint8Array.from(raw, (ch) => ch.charCodeAt(0));
  if (result.length > max || encode(result) !== value) throw new Error("Invalid encoding");
  return result;
}
function aad(scope: string, direction: "request" | "response"): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9:._-]{1,512}$/.test(scope)) throw new Error("Invalid scope");
  return textEncoder.encode(`analytics-collection-v1\0${scope}\0${direction}`);
}
async function encrypt(
  value: unknown,
  scope: string,
  direction: "request" | "response",
): Promise<EncryptedRequest> {
  const plaintext = textEncoder.encode(JSONR.stringify(value));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: aad(scope, direction), tagLength: 128 },
    await key(),
    plaintext,
  );
  return EncryptedRequestSchema.parse({
    version: 1,
    iv: encode(iv),
    ciphertext: encode(new Uint8Array(ciphertext)),
  });
}
export class CollectionPlaintextError extends Error {
  constructor() {
    super("Invalid collection plaintext");
  }
}
async function decrypt(
  input: unknown,
  scope: string,
  direction: "request" | "response",
  maxPlaintextBytes: number,
): Promise<unknown> {
  const envelope = EncryptedRequestSchema.parse(input);
  const iv = decode(envelope.iv, 12),
    ciphertext = decode(envelope.ciphertext, 65552);
  if (iv.length !== 12 || ciphertext.length < 16) throw new Error("Invalid encrypted message");
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv, additionalData: aad(scope, direction), tagLength: 128 },
    await key(),
    ciphertext,
  );
  try {
    if (
      !Number.isSafeInteger(maxPlaintextBytes) ||
      maxPlaintextBytes < 1 ||
      maxPlaintextBytes > 65536 ||
      plaintext.byteLength > maxPlaintextBytes
    )
      throw new Error("Invalid length");
    return JSONR.parse(new TextDecoder("utf-8", { fatal: true }).decode(plaintext));
  } catch {
    throw new CollectionPlaintextError();
  }
}
export async function encryptRequest(
  value: unknown,
  scope: string,
): Promise<{ envelope: EncryptedRequest }> {
  return { envelope: await encrypt(value, scope, "request") };
}
export async function decryptRequest(
  input: unknown,
  scope: string,
  maxPlaintextBytes = 65536,
): Promise<{ value: unknown }> {
  return { value: await decrypt(input, scope, "request", maxPlaintextBytes) };
}
export async function encryptResponse(value: unknown, scope: string): Promise<EncryptedResponse> {
  return encrypt(value, scope, "response");
}
export async function decryptResponse(input: unknown, scope: string): Promise<unknown> {
  return decrypt(input, scope, "response", 65536);
}

// Config selects this protocol. The external bytes have no magic, version or application marker.
// Request: RSA-OAEP wrapped key (256) | random IV (12) | AES-GCM ciphertext + tag (16).
// Response: random IV (12) | AES-GCM ciphertext + tag (16), using the request key.
export const BINARY_REQUEST_OVERHEAD_BYTES = 256 + 12 + 16;
export const BINARY_REQUEST_MAX_BYTES = BINARY_REQUEST_OVERHEAD_BYTES + 65536;
export const BINARY_RESPONSE_MAX_BYTES = 12 + 65536 + 16;
export type BinaryCollectionScope = { readonly hostname: string; readonly path: string };
export type BinaryRequestContext = {
  readonly key: CryptoKey;
  readonly scope: BinaryCollectionScope;
  readonly requestIv: Uint8Array<ArrayBuffer>;
};
export class BinaryCollectionPlaintextError extends CollectionPlaintextError {
  constructor(readonly context: BinaryRequestContext) {
    super();
  }
}

function binaryAad(
  scope: BinaryCollectionScope,
  direction: "request" | "response",
  requestIv?: Uint8Array,
): Uint8Array<ArrayBuffer> {
  const { hostname, path } = BinaryScopeSchema.parse(scope);
  return textEncoder.encode(JSON.stringify([
    "xx.domain-transport", 1, hostname, path, direction,
    ...(requestIv ? [encode(requestIv)] : []),
  ]));
}
function joinBytes(...parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const joined = new Uint8Array(parts.reduce((sum, part) => sum + part.byteLength, 0));
  let offset = 0;
  for (const part of parts) {
    joined.set(part, offset);
    offset += part.byteLength;
  }
  return joined;
}
function binaryInput(input: Uint8Array, min: number, max: number): Uint8Array<ArrayBuffer> {
  if (!(input instanceof Uint8Array) || input.byteLength < min || input.byteLength > max)
    throw new Error("Invalid message length");
  // Snapshot before asynchronous crypto, including callers' SharedArrayBuffer-backed views.
  return new Uint8Array(input);
}
function parseBinaryPlaintext(plaintext: ArrayBuffer, max: number): unknown {
  if (!Number.isSafeInteger(max) || max < 1 || max > 65536 || plaintext.byteLength > max)
    throw new CollectionPlaintextError();
  return JSONR.parse(new TextDecoder("utf-8", { fatal: true }).decode(plaintext));
}
const BinaryPrivateKeySchema = AnalyticsPublicKeySchema.omit({ key_ops: true, ext: true }).extend({
  d: encoded(342), p: encoded(171), q: encoded(171),
  dp: encoded(171), dq: encoded(171), qi: encoded(171),
  ext: z.boolean().optional(),
  key_ops: z.tuple([z.literal("decrypt")]).optional(),
});
async function importBinaryPrivateKey(input: unknown): Promise<CryptoKey> {
  const { kty, alg, n, e, d, p, q, dp, dq, qi, ext } = BinaryPrivateKeySchema.parse(input);
  return crypto.subtle.importKey(
    "jwk", { kty, alg, n, e, d, p, q, dp, dq, qi, ext: ext ?? true, key_ops: ["decrypt"] },
    { name: "RSA-OAEP", hash: "SHA-256" }, false, ["decrypt"],
  );
}

/** Browser: create a new AES-256 key and IV for every send, including retries. */
export async function encryptBinaryRequest(
  value: unknown,
  config: BrowserAnalyticsConfig,
): Promise<{ body: Uint8Array<ArrayBuffer>; context: BinaryRequestContext }> {
  const parsed = BrowserAnalyticsConfigSchema.parse(config);
  const scope = { hostname: parsed.hostname, path: parsed.path };
  const additionalData = binaryAad(scope, "request");
  const plaintext = textEncoder.encode(JSONR.stringify(value));
  const { kty, alg, n, e } = parsed.publicKey;
  const publicKey = await crypto.subtle.importKey(
    "jwk", { kty, alg, n, e, ext: true, key_ops: ["encrypt"] },
    { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"],
  );
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const wrapped = await crypto.subtle.encrypt(
    { name: "RSA-OAEP", label: additionalData }, publicKey, await crypto.subtle.exportKey("raw", key),
  );
  const requestIv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: requestIv, additionalData, tagLength: 128 }, key, plaintext,
  );
  return {
    body: joinBytes(new Uint8Array(wrapped), requestIv, new Uint8Array(ciphertext)),
    context: { key, scope, requestIv },
  };
}

/** Receiver: only the separately held per-domain private JWK can open a request. */
export async function decryptBinaryRequest(
  input: Uint8Array,
  scope: BinaryCollectionScope,
  privateKey: unknown,
  maxPlaintextBytes = 61440,
): Promise<{ value: unknown; context: BinaryRequestContext }> {
  const data = binaryInput(input, 256 + 12 + 16, BINARY_REQUEST_MAX_BYTES);
  const parsedScope = BinaryScopeSchema.parse(scope);
  const additionalData = binaryAad(parsedScope, "request");
  const raw = await crypto.subtle.decrypt(
    { name: "RSA-OAEP", label: additionalData }, await importBinaryPrivateKey(privateKey), data.subarray(0, 256),
  );
  if (raw.byteLength !== 32) throw new Error("Invalid key length");
  const key = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
  const requestIv = data.slice(256, 268);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: requestIv, additionalData, tagLength: 128 }, key, data.subarray(268),
  );
  const context = { key, scope: parsedScope, requestIv };
  try {
    return { value: parseBinaryPlaintext(plaintext, maxPlaintextBytes), context };
  } catch {
    throw new BinaryCollectionPlaintextError(context);
  }
}

export async function encryptBinaryResponse(
  value: unknown,
  context: BinaryRequestContext,
): Promise<Uint8Array<ArrayBuffer>> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({
    name: "AES-GCM", iv, tagLength: 128,
    additionalData: binaryAad(context.scope, "response", context.requestIv),
  }, context.key, textEncoder.encode(JSONR.stringify(value)));
  return joinBytes(iv, new Uint8Array(ciphertext));
}
export async function decryptBinaryResponse(
  input: Uint8Array,
  context: BinaryRequestContext,
): Promise<unknown> {
  const data = binaryInput(input, 12 + 16, BINARY_RESPONSE_MAX_BYTES);
  const plaintext = await crypto.subtle.decrypt({
    name: "AES-GCM", iv: data.subarray(0, 12), tagLength: 128,
    additionalData: binaryAad(context.scope, "response", context.requestIv),
  }, context.key, data.subarray(12));
  return parseBinaryPlaintext(plaintext, 65536);
}
